import React from 'react';
import { useQuery } from '@tanstack/react-query';
import { ArrowUp, ArrowDown, ArrowUpDown } from 'lucide-react';

import type { WorkflowCatalogEntry } from '../api/workflowsApi';
import { fetchWorkflowsV2Api } from '../api/workflowsV2Api';
import type { Worker } from '../api/workersApi';
import DarkDataTable from '@/shared/components/ui/DarkDataTable';
import DarkMinimalFilter from '@/shared/components/ui/DarkMinimalFilter';
import { timeAgo } from '@/utils/date';
import CreateWorkflowDialog from './CreateWorkflowDialog';
import WorkflowBuilderPanel from './workflowBuilder/WorkflowBuilderPanel';

// ─── Sort Header Button ───────────────────────────────────────────────────────
type SortKey = 'workflow_key' | 'version' | 'updated_at';

function SortBtn({ label, active, dir, onSort }: {
  label: string;
  active: boolean;
  dir: 'asc' | 'desc';
  onSort: () => void;
}) {
  return (
    <button
      type="button"
      className="group inline-flex items-center gap-1 select-none text-left focus:outline-none"
      onClick={onSort}
    >
      <span className={active ? 'text-neutral-200 font-medium' : 'text-neutral-300 group-hover:text-neutral-500 transition-colors'}>
        {label}
      </span>
      <span className={active ? 'text-neutral-400' : 'text-neutral-600 group-hover:text-neutral-800 transition-colors'}>
        {active ? (dir === 'asc' ? <ArrowUp className="h-3 w-3" /> : <ArrowDown className="h-3 w-3" />) : <ArrowUpDown className="h-3 w-3" />}
      </span>
    </button>
  );
}

type Props = {
  workers: Worker[];
  workersLoading: boolean;
  workersError: Error | null;
};

/**
 * Workflow management (v2 list) with the same look/feel as the Developer Workflow Runner list:
 * - Active-only list
 * - Click row expands inline details (here: the Builder)
 */
export default function WorkflowManagementAccordionV2({ workers, workersLoading, workersError }: Props) {
  const [openDialog, setOpenDialog] = React.useState(false);
  const [createdWorkflow, setCreatedWorkflow] = React.useState<WorkflowCatalogEntry | null>(null);
  const [filterText, setFilterText] = React.useState('');

  // ─── Sort State ──────────────────────────────────────────────────────
  const [sortKey, setSortKey] = React.useState<SortKey | ''>('');
  const [sortDir, setSortDir] = React.useState<'asc' | 'desc'>('asc');

  const cycleSortKey = React.useCallback((key: SortKey) => {
    if (sortKey !== key) { setSortKey(key); setSortDir('asc'); }
    else if (sortDir === 'asc') { setSortDir('desc'); }
    else { setSortKey(''); setSortDir('asc'); }
  }, [sortKey, sortDir]);

  const { data: workflows = [], isLoading, error } = useQuery<WorkflowCatalogEntry[], Error>({
    queryKey: ['admin', 'orchestrator', 'workflows', 'v2', 'active-only'],
    queryFn: async () => {
      const res = await fetchWorkflowsV2Api({ limit: 500 });
      const items = (res.items ?? []) as unknown as WorkflowCatalogEntry[];
      return items.filter((w) => w?.is_active === true);
    },
    refetchInterval: 30_000,
    refetchIntervalInBackground: true,
  });

  const sorted = React.useMemo(() => {
    return workflows
      .slice()
      .sort((a, b) => {
        const ak = String(a?.workflow_key ?? '').localeCompare(String(b?.workflow_key ?? ''));
        if (ak !== 0) return ak;
        return Number(b?.version ?? 0) - Number(a?.version ?? 0);
      });
  }, [workflows]);

  const rowsForTable = React.useMemo(() => {
    // Include recently-created workflow immediately, even before the list refetch catches up.
    if (!createdWorkflow) return sorted;
    const createdKey = keyOf(createdWorkflow);
    const exists = sorted.some((w) => keyOf(w) === createdKey);
    return exists ? sorted : [createdWorkflow, ...sorted];
  }, [createdWorkflow, sorted]);

  const filteredRowsForTable = React.useMemo(() => {
    const t = filterText.trim().toLowerCase();
    const filtered = !t ? rowsForTable : rowsForTable.filter((w) => String(w?.workflow_key ?? '').toLowerCase().includes(t));
    if (!sortKey) return filtered;
    return filtered.slice().sort((a, b) => {
      let cmp = 0;
      if (sortKey === 'workflow_key') {
        cmp = String(a.workflow_key ?? '').localeCompare(String(b.workflow_key ?? ''), undefined, { numeric: true, sensitivity: 'base' });
      } else if (sortKey === 'version') {
        cmp = Number(a.version ?? 0) - Number(b.version ?? 0);
      } else if (sortKey === 'updated_at') {
        cmp = String(a.updated_at ?? '').localeCompare(String(b.updated_at ?? ''));
      }
      return sortDir === 'asc' ? cmp : -cmp;
    });
  }, [filterText, rowsForTable, sortKey, sortDir]);

  const [selectedKey, setSelectedKey] = React.useState<string | null>(null);
  const [closingKey, setClosingKey] = React.useState<string | null>(null);
  const closeTimerRef = React.useRef<number | null>(null);

  React.useEffect(() => {
    return () => {
      if (closeTimerRef.current != null) window.clearTimeout(closeTimerRef.current);
    };
  }, []);

  React.useEffect(() => {
    // If the filter hides the currently-selected workflow, close the inline builder.
    if (!selectedKey) return;
    const stillVisible = filteredRowsForTable.some((w) => keyOf(w) === selectedKey);
    if (stillVisible) return;
    if (closeTimerRef.current != null) window.clearTimeout(closeTimerRef.current);
    setSelectedKey(null);
    setClosingKey(null);
  }, [filteredRowsForTable, selectedKey]);

  const visibleKey = selectedKey ?? closingKey;
  const selected = React.useMemo(() => {
    if (!visibleKey) return null;
    const found = sorted.find((w) => keyOf(w) === visibleKey) ?? null;
    if (found) return found;
    if (createdWorkflow && keyOf(createdWorkflow) === visibleKey) return createdWorkflow;
    return null;
  }, [createdWorkflow, sorted, visibleKey]);

  return (
    <div
      className={
        'pac-glass rounded-md flex flex-col min-h-0 ' +
        // single combined panel (list + inline builder)
        'flex-1'
      }
    >
      <div className="px-3 py-2 border-b border-neutral-800 flex items-center justify-between gap-3">
        <div className="text-sm text-neutral-200">Workflows</div>
        <div className="flex items-center gap-3">
          <div className="text-xs text-neutral-500">Active only</div>
          <div className="w-56">
            <DarkMinimalFilter
              value={filterText}
              onChange={setFilterText}
              placeholder="Filter…"
            />
          </div>
          <button
            type="button"
            className="px-3 py-1.5 rounded-md pac-glass text-xs text-neutral-200 hover:border-neutral-600"
            onClick={() => setOpenDialog(true)}
          >
            New
          </button>
        </div>
      </div>

      {isLoading && <div className="p-3 text-sm text-neutral-400">Loading workflows…</div>}
      {error && <div className="p-3 text-sm text-rose-400">Failed to load workflows: {error.message}</div>}

      {!isLoading && !error && (
        <div className="flex-1 min-h-0 overflow-y-auto">
          <div className="p-3">
            <DarkDataTable<WorkflowCatalogEntry>
              frame={false}
              columns={[
                {
                  header: <SortBtn label="Workflow" active={sortKey === 'workflow_key'} dir={sortDir} onSort={() => cycleSortKey('workflow_key')} />,
                  tdClassName: 'text-neutral-200',
                  render: (w) => (
                    <div className="truncate" title={String(w.workflow_key ?? '')}>{String(w.workflow_key ?? '')}</div>
                  ),
                },
                {
                  header: <SortBtn label="Version" active={sortKey === 'version'} dir={sortDir} onSort={() => cycleSortKey('version')} />,
                  thClassName: 'whitespace-nowrap',
                  tdClassName: 'text-neutral-400 whitespace-nowrap',
                  render: (w) => `v${Number(w.version ?? 0)}`,
                },
                {
                  header: <SortBtn label="Updated" active={sortKey === 'updated_at'} dir={sortDir} onSort={() => cycleSortKey('updated_at')} />,
                  thClassName: 'whitespace-nowrap',
                  tdClassName: 'text-neutral-500 whitespace-nowrap',
                  render: (w) => {
                    const ts = String((w as any)?.updated_at ?? '').trim();
                    if (!ts) return '—';
                    return <span title={formatDateShort(ts)}>{timeAgo(ts)}</span>;
                  },
                },
              ]}
              rows={filteredRowsForTable}
              emptyMessage="No active workflows published."
              getRowKey={(w) => keyOf(w)}
              onRowClick={(w) => {
                const k = keyOf(w);
                const active = k === selectedKey;

                if (active) {
                  if (closeTimerRef.current != null) window.clearTimeout(closeTimerRef.current);
                  setClosingKey(k);
                  setSelectedKey(null);
                  closeTimerRef.current = window.setTimeout(() => {
                    setClosingKey((curr) => (curr === k ? null : curr));
                    closeTimerRef.current = null;
                  }, 180);
                  return;
                }

                if (closeTimerRef.current != null) window.clearTimeout(closeTimerRef.current);
                setClosingKey(null);
                setSelectedKey(k);
              }}
              isRowSelected={(w) => keyOf(w) === selectedKey}
              isRowExpanded={(w) => {
                const k = keyOf(w);
                const active = k === selectedKey;
                const closing = k === closingKey && selectedKey === null;
                return active || closing;
              }}
              renderExpandedRow={(w) => {
                const k = keyOf(w);
                const active = k === selectedKey;
                const closing = k === closingKey && selectedKey === null;
                const showInlineBuilder = active || closing;
                const builderOpen = active;

                if (!showInlineBuilder || !selected || keyOf(selected) !== k) return null;

                return (
                  <div
                    className={
                      'border-t border-neutral-800 pac-glass overflow-hidden ' +
                      'transition-[max-height,opacity] duration-200 ease-out ' +
                      (builderOpen ? 'max-h-[2000px] opacity-100' : 'max-h-0 opacity-0 pointer-events-none')
                    }
                  >
                    <div className="px-3 py-3">
                      <div className="h-[820px] min-h-0 overflow-hidden rounded-md pac-glass">
                        <div className="h-full min-h-0 overflow-hidden p-3">
                          <WorkflowBuilderPanel
                            handlersSource="v2"
                            selected={selected}
                            workers={workers}
                            workersLoading={workersLoading}
                            workersError={workersError}
                          />
                        </div>
                      </div>
                    </div>
                  </div>
                );
              }}
            />
          </div>
        </div>
      )}

      <CreateWorkflowDialog
        open={openDialog}
        onOpenChange={setOpenDialog}
        onCreated={(wf) => {
          setCreatedWorkflow(wf);
          setSelectedKey(keyOf(wf));
          setClosingKey(null);
        }}
      />
    </div>
  );
}

function keyOf(w: WorkflowCatalogEntry) {
  return `${w.workflow_key}::${w.version}`;
}

function formatDateShort(value: string) {
  if (!value) return '—';
  try {
    const d = new Date(value);
    if (Number.isNaN(d.getTime())) return value;
    return d.toLocaleString();
  } catch {
    return value;
  }
}
